/**
 * Cultural Aspects Utilities
 * Helper functions for parsing and transforming cultural aspects data
 */

import { CulturalAspect } from './types';

/**
 * Extracts a meaningful heading from a description string
 * Looks for capitalized phrases, key terms, or first sentence
 */
function extractHeading(description: string): string {
  const trimmed = description.trim();

  // Look for patterns like "Bustling street markets" -> "Street Markets"
  // or "Numerous Buddhist temples" -> "Buddhist Temples"
  const words = trimmed.split(' ');

  // Find capitalized words or phrases at the start
  const headingWords: string[] = [];
  for (let i = 0; i < Math.min(words.length, 4); i++) {
    const word = words[i];
    // Check if word starts with capital (excluding first word which always does)
    if (i === 0 || (word[0] && word[0] === word[0].toUpperCase())) {
      headingWords.push(word);
    } else {
      break;
    }
  }

  // If we found a good phrase (2-3 words), use it
  if (headingWords.length >= 2 && headingWords.length <= 4) {
    // Clean up common articles and prepositions
    const cleaned = headingWords
      .filter(w => !['The', 'A', 'An', 'In', 'On', 'At'].includes(w))
      .slice(0, 3);

    if (cleaned.length >= 2) {
      return cleaned.join(' ');
    }
  }

  // Fallback: use first 2-3 words, capitalizing appropriately
  const firstWords = words.slice(0, 3).join(' ');
  return firstWords.length > 30
    ? firstWords.substring(0, 30) + '...'
    : firstWords;
}

/**
 * Parses a string item into a CulturalAspect object
 * Handles formats like:
 * - "Heading: Description"
 * - "Description" (extracts heading from description)
 *
 * @param item - String item to parse
 * @param index - Index for generating ID if needed
 * @returns Parsed CulturalAspect object
 */
export function parseCulturalAspectItem(
  item: string,
  index: number
): CulturalAspect {
  const trimmed = item.trim();
  if (!trimmed) {
    return {
      id: `cultural-aspect-${index}`,
      heading: 'Cultural Aspect',
      description: trimmed,
    };
  }

  // Check if item contains a colon (indicating heading:description format)
  const colonIndex = trimmed.indexOf(':');

  if (colonIndex > 0 && colonIndex < trimmed.length - 1) {
    // Has explicit heading and description
    const heading = trimmed.substring(0, colonIndex).trim();
    const description = trimmed.substring(colonIndex + 1).trim();

    if (heading && description) {
      return {
        id: `cultural-aspect-${index}`,
        heading,
        description,
      };
    }
  }

  // No explicit heading, extract from description
  const heading = extractHeading(trimmed);
  const description = trimmed;

  return {
    id: `cultural-aspect-${index}`,
    heading,
    description,
  };
}

/**
 * Parses an array of string items into CulturalAspect objects
 *
 * @param items - Array of string items
 * @returns Array of parsed CulturalAspect objects
 */
export function parseCulturalAspectItems(items: string[]): CulturalAspect[] {
  if (!items || items.length === 0) {
    return [];
  }

  return items.map((item, index) => parseCulturalAspectItem(item, index));
}
